// ============================================================================
// Edit a number — the ONE component that writes a correction (§15a).
// Decision 1: "There must be an 'edit' capability if the 'calculation' is
// wrong." The person types the right number and a reason; the log keeps the
// original and records who changed it and why, and everything downstream —
// shelf, usage, days left, cost — recomputes from the corrected entry.
// ⛔ The draft resets on open and after a successful save.
// ============================================================================

import { useEffect, useState } from 'react';
import { correctEntry } from './api';
import { fmtQty } from './format';
import { FIELD_CLASS, QtyField, StockWindow, parseQty } from './stock-window';

export interface CorrectionTarget {
  movementId: string;
  /** "the Sep 8 tab count", "the delivery on Sep 8", "the received quantity" */
  what: string;
  currentQty: number;
  unitLabel: string | null;
}

export function CorrectEntryWindow({
  open,
  onClose,
  target,
}: {
  open: boolean;
  onClose: () => void;
  target: CorrectionTarget | null;
}) {
  const [qty, setQty] = useState('');
  const [reason, setReason] = useState('');
  useEffect(() => {
    if (open && target) {
      setQty(String(target.currentQty));
      setReason('');
    }
  }, [open, target]);

  if (!target) return null;
  const n = parseQty(qty);
  const canSave = n != null && n !== target.currentQty && reason.trim().length > 0;

  async function save() {
    if (!target || n == null) return;
    await correctEntry(target.movementId, n, reason.trim());
    setQty('');
    setReason('');
    onClose();
  }

  return (
    <StockWindow
      open={open}
      title="Correct a number"
      subtitle={`Correcting ${target.what} — now ${fmtQty(target.currentQty, target.unitLabel)}`}
      canSave={canSave}
      saveLabel="Save correction"
      onCancel={onClose}
      onSave={save}
    >
      <QtyField
        label={target.unitLabel === 'tabs' ? 'The right number, tabs' : 'The right number, gallons'}
        value={qty}
        onChange={setQty}
        step={target.unitLabel === 'tabs' ? 1 : 0.1}
      />
      <label className="block">
        <span className="mb-1 block text-xs text-tcs-text-muted">Why? (required)</span>
        <input className={FIELD_CLASS} placeholder="miscounted" value={reason} onChange={(e) => setReason(e.target.value)} />
      </label>
      <p className="text-[11px] text-tcs-text-muted">
        The original stays in the history with your correction beside it. Usage, days left and cost recalculate from the
        corrected number.
      </p>
    </StockWindow>
  );
}
